import { useState, useEffect } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import Buttons from '../buttons';
import Image from '@klv/atoms/image';
import { generateClassName, hasImage } from '@klv/shared/utils/functions';
import { createButtonsArray, setButtonColor, setTextHoverColor } from './utils/functions';
import relatedContentCard from './themes/default';

import * as s from './styles/relatedContentCard.module.scss'

import type { IRelatedContentCard, TRelatedContentCardHoverColor } from './types';

const RelatedContentCard = ({
  bodyCopy, buttons, eyebrow, headline, hoverImage, link, solidCardHoverColor = 'none', variant, ...rest
}: IRelatedContentCard) => {
  const cardButtons = buttons ? createButtonsArray({ buttons, url: link }) : [];
  const hasHoverImage = hasImage(hoverImage);
  const [isHovered, setIsHovered] = useState(false);
  const openInNewTab = buttons?.length && buttons[0].openInNewTab;
  const { t } = useTranslation();

  const [currentButtonColor, setCurrentButtonColor] = useState(setButtonColor({
    hasHoverImage, isHovered, variant, solidCardHoverColor,
  }));

  useEffect(() => {
    setCurrentButtonColor(setButtonColor({
      hasHoverImage, isHovered, variant, solidCardHoverColor,
    }));
  }, [hasHoverImage, isHovered, solidCardHoverColor, variant]);

  return (
    <article
      className={
        generateClassName([
          s.relatedContentCard,
          s[variant],
          hasHoverImage && s.hasImage,
        ])
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        '--color-solid-card-hover-color':
          relatedContentCard[variant].solidCardHoverColor[solidCardHoverColor as keyof TRelatedContentCardHoverColor],
        '--color-solid-card-text-hover': setTextHoverColor({
          $hasImage: hasHoverImage,
          $solidCardHoverColor: solidCardHoverColor,
          $variant: variant,
        }),
      } as React.CSSProperties}
      {...rest}
    >
      <div className={generateClassName([s.eyebrow, s[variant], "eyebrow"])}>
        {eyebrow}
      </div>
      <a
        className={s.relatedContentLink}
        aria-label={openInNewTab ? `${headline} (${t('molecules_relatedCard_openInNewTab')}
        )` : ''}
        href={link}
        onBlur={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        target={openInNewTab ? '_blank' : '_self'}
      >
        <h3 className={generateClassName([s.headline, s[variant], 'editorialHeadline'])}>
          {headline}
        </h3>
      </a>
      {bodyCopy && (
        <div
          className={generateClassName([s.bodyCopy, s[variant], "p2"])}
          dangerouslySetInnerHTML={{ __html: bodyCopy }}
        />
      )}
      <Buttons
        className={s.buttons}
        aria-hidden
        buttonColor={currentButtonColor}
        setTabIndex={-1}
        buttons={cardButtons}
        variants={['tertiary']}
      />
      {hoverImage?.gatsbyImage && (
        <div className={s.hoverImageContainer}>
          <Image altText={hoverImage?.altText || ''} imageData={hoverImage.gatsbyImage} />
        </div>
      )}
    </article>
  );
};

export default RelatedContentCard;
